/**
 * Linear Regression R² Calculator
 *
 * Calculates the coefficient of determination (R²) for a linear regression
 * over a rolling window. R² measures how well the price data fits a linear
 * trend line, with values ranging from 0 (no correlation) to 1 (perfect fit).
 *
 * Formula:
 * - R² = slope² × scalingFactor
 * - slope (b) = (N * Σ(w*y) - Σw * Σy) / (N * Σ(w²) - (Σw)²)
 * - scalingFactor (c) = (N * Σ(w²) - (Σw)²) / (N * Σ(y²) - (Σy)²)
 *
 * Where:
 * - N = period (windowSize)
 * - w = weight (1, 2, 3, ... N)
 * - y = price values
 *
 * Range: 0 to 1
 * - 0 = No linear correlation (random movement)
 * - 1 = Perfect linear correlation (strong trend)
 */
export default function () {
  function calculator(data) {
    const { period, sourcePath } = options;

    if (!data || data.length < period) {
      return new Array(data.length).fill(undefined);
    }

    // Pre-calculate constants for linear regression
    const sumWeights = (period * (period + 1)) / 2;
    const squaredSumWeights = Math.pow(sumWeights, 2);
    const sumWeightsSquared = (sumWeights * (2 * period + 1)) / 3;

    let sumCloses = 0;
    let sumWeightedCloses = 0;
    let sumClosesSquared = 0;

    const results = [];

    for (let i = 0; i < data.length; i++) {
      const d = data[i];

      // Get source value
      const currentValue = sourcePath ? d[sourcePath] : d.close;

      if (currentValue == null) {
        results.push(undefined);
        continue;
      }

      // Update running sums
      sumWeightedCloses += period * currentValue - sumCloses;
      sumCloses += currentValue;
      sumClosesSquared += Math.pow(currentValue, 2);

      // Need at least period bars
      if (i < period - 1) {
        results.push(undefined);
        continue;
      }

      // Remove oldest value if beyond window
      if (i > period - 1) {
        const oldValue = sourcePath
          ? data[i - period][sourcePath]
          : data[i - period].close;

        if (oldValue != null) {
          sumCloses -= oldValue;
          sumClosesSquared -= Math.pow(oldValue, 2);
        }
      }

      // Calculate slope (b)
      const numerator = period * sumWeightedCloses - sumWeights * sumCloses;
      const denominator = period * sumWeightsSquared - squaredSumWeights;
      const slope = numerator / denominator;

      // Calculate scaling factor (c)
      const denominatorC = period * sumClosesSquared - Math.pow(sumCloses, 2);

      // Avoid division by zero
      if (denominatorC === 0) {
        results.push(undefined);
        continue;
      }

      const scalingFactor = denominator / denominatorC;

      // Calculate R² = slope² × scalingFactor
      const rSquared = Math.pow(slope, 2) * scalingFactor;

      // Clamp to [0, 1] range (numerical precision issues)
      const clampedRSquared = Math.max(0, Math.min(1, rSquared));

      results.push(clampedRSquared);
    }

    return results;
  }

  let options = {
    period: 14,
    sourcePath: "close",
  };

  calculator.undefinedLength = function () {
    return options.period - 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...options, ...x };
    return calculator;
  };

  return calculator;
}
